import React from "react";
import { Link } from "react-router-dom";
import GenericTable from "@surgia/ui/containers/table/generic-table";
import TruncatedTooltip from "@surgia/ui/components/truncatedTooltip";
import { TableColumn } from "@surgia/ui/containers/table/constants";
import { formatDateWithMoment } from "@surgia/utils";

interface IBranchesTableProps {
  data: any;
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  hasRequiredEditPermission: boolean;
  // New sorting props
  sortState?: { column: string | null; direction: "asc" | "desc" | null };
  onSort?: (sortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => void;
}

const BranchesTable: React.FC<IBranchesTableProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  hasRequiredEditPermission,
  sortState,
  onSort,
}) => {
  const columns: TableColumn[] = [
    {
      key: "id",
      translationKey: "ID",
      sortable: true,
      sortType: "number",
      sortKey: "id",
      render: (val: any, row: any) =>
        hasRequiredEditPermission ? (
          <div className="relative group">
            <Link
              to={`/branches/${row.id}/${row.name}`}
              className="cursor-pointer"
            >
              {val}
            </Link>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
              {`Edit ${val}`}
            </div>
          </div>
        ) : (
          <span>{val}</span>
        ),
    },
    {
      key: "name",
      translationKey: "Branch name",
      sortable: true,
      sortType: "string",
      sortKey: "name", // API field name for sorting
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
    },
    {
      key: "city",
      translationKey: "City",
      sortable: true,
      sortType: "string",
      sortKey: "address.city.name",
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
      // No sortKey - will use internal sorting
    },
    {
      key: "area",
      translationKey: "Area",
      sortable: true,
      sortType: "string",
      sortKey: "address.area.name",
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
    },
    {
      key: "branch_users_count",
      translationKey: "Users",
      sortable: true,
      sortType: "number",
      sortKey: "usersCount",
    },
    {
      key: "contact_person_full_name",
      translationKey: "Contact name",
      sortable: true,
      sortType: "string",
      sortKey: "contactPersonFullName",
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
    },
    {
      key: "contact_person_mobile",
      translationKey: "Contact details",
      sortable: false,
      render: (row: any) => (
        <div className="text-gray-500">
          <TruncatedTooltip
            content={row.mobile}
            useTableRowMain={true}
            as="p"
          />
          <TruncatedTooltip content={row.email} useTableRowMain={true} as="p" />
        </div>
      ),
    },
    {
      key: "created_at",
      translationKey: "Date created",
      sortable: true,
      sortType: "date",
      sortKey: "createdAt",
      render: (row: any) => formatDateWithMoment(row),
    },
  ];

  return (
    <GenericTable
      columns={columns}
      data={data}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortState={sortState}
      onSort={onSort}
      enableInternalSorting={false}
    />
  );
};

export default BranchesTable;
